import { z } from 'zod';
import { CASH_DRAWER_KINDS, EXPENSE_METHODS, PAYROLL_RUN_STATUSES, RECURRING_EXPENSE_FREQUENCIES } from '@bulk/types';
import { dateOnlySchema, idSchema, pageSchema, patchOf, searchQuerySchema, sortOrderSchema } from './common';
import { monthSchema } from './timesheets';

const localizedNames = z.record(z.string(), z.string().trim().max(80));
const amountSchema = z.number().int().positive().max(1_000_000_000).describe('Minor units of the gym currency');
const textOrNull = (max: number) => z.string().trim().max(max).nullable().optional();

export const EXPENSE_WHOLE_GYM = 'none';

export const expenseCategorySchema = z.object({
  id: idSchema,
  key: z.string().nullable().describe('The default category this row was made from; null for one the gym added'),
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  active: z.boolean(),
  sortOrder: z.number().int(),
});
export type ExpenseCategoryDto = z.infer<typeof expenseCategorySchema>;

export const createExpenseCategorySchema = z.object({
  name: z.string().trim().min(1).max(80).describe('English name'),
  nameLocalized: localizedNames.default({}),
  active: z.boolean().default(true).describe('Off archives it: it stays on old expenses and leaves the pickers'),
  sortOrder: z.number().int().min(0).max(10_000).optional(),
});
export type CreateExpenseCategoryInput = z.infer<typeof createExpenseCategorySchema>;
export const updateExpenseCategorySchema = patchOf(createExpenseCategorySchema);
export type UpdateExpenseCategoryInput = z.infer<typeof updateExpenseCategorySchema>;

export const createExpenseSchema = z.object({
  expenseDate: dateOnlySchema.optional().describe("The gym's calendar date it was spent; today when left out, and always today when paid from the drawer"),
  amountMinor: amountSchema,
  categoryId: idSchema,
  locationId: idSchema.nullable().optional().describe("The branch it was spent for; null for the whole gym. Paid from the drawer: the drawer's branch, the one the request works at when left out"),
  supplier: textOrNull(160),
  method: z.enum(EXPENSE_METHODS),
  note: textOrNull(2000),
  fromDrawer: z.boolean().default(false).describe("Cash taken from the branch's open drawer, lowering the shift's expected cash (expenses.cash)"),
  drawer: z.enum(CASH_DRAWER_KINDS).default('DESK').describe("With fromDrawer, which drawer pays: the desk's, or the bar's (expenses.cash and bar.sell)"),
});
export type CreateExpenseInput = z.infer<typeof createExpenseSchema>;

export const updateExpenseSchema = patchOf(createExpenseSchema.omit({ fromDrawer: true, drawer: true }));

export const drawerExpenseQuerySchema = z.object({
  drawer: z.enum(CASH_DRAWER_KINDS).default('DESK'),
});
export type DrawerExpenseQuery = z.infer<typeof drawerExpenseQuerySchema>;
export type UpdateExpenseInput = z.infer<typeof updateExpenseSchema>;

export const voidExpenseSchema = z.object({ reason: z.string().trim().min(1).max(500) });
export type VoidExpenseInput = z.infer<typeof voidExpenseSchema>;

export const EXPENSE_STATUS_FILTERS = ['ALL', 'ACTIVE', 'VOIDED'] as const;
export type ExpenseStatusFilter = (typeof EXPENSE_STATUS_FILTERS)[number];

export const EXPENSE_LIST_SORTS = ['expenseDate', 'amountMinor', 'createdAt'] as const;
export type ExpenseListSort = (typeof EXPENSE_LIST_SORTS)[number];

export const expenseListQuerySchema = searchQuerySchema.extend({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  categoryId: idSchema.optional(),
  locationId: z.union([idSchema, z.literal(EXPENSE_WHOLE_GYM)]).optional().describe('A branch, or `none` for expenses of the whole gym'),
  method: z.enum(EXPENSE_METHODS).optional(),
  status: z.enum(EXPENSE_STATUS_FILTERS).default('ALL').describe('Voided expenses stay listed; they never count'),
  sort: z.enum(EXPENSE_LIST_SORTS).default('expenseDate'),
  order: sortOrderSchema.default('desc'),
});
export type ExpenseListQuery = z.infer<typeof expenseListQuerySchema>;

export const expenseAttachmentSchema = z.object({
  id: idSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int(),
  uploadedAt: z.string(),
  uploadedByName: z.string().nullable(),
});
export type ExpenseAttachmentDto = z.infer<typeof expenseAttachmentSchema>;

export const expenseAttachmentLinkSchema = z.object({
  url: z.string().describe('Signed and short-lived; fetch it right away'),
  fileName: z.string(),
  mimeType: z.string(),
});
export type ExpenseAttachmentLinkDto = z.infer<typeof expenseAttachmentLinkSchema>;

export const expenseSchema = z.object({
  id: idSchema,
  expenseDate: z.string(),
  amountMinor: z.number().int(),
  currency: z.string(),
  categoryId: idSchema,
  categoryName: z.string(),
  categoryNameLocalized: z.record(z.string(), z.string()),
  locationId: idSchema.nullable(),
  locationName: z.string().nullable(),
  supplier: z.string().nullable(),
  method: z.enum(EXPENSE_METHODS),
  note: z.string().nullable(),
  fromDrawer: z.boolean(),
  cashShiftId: idSchema.nullable(),
  recurringExpenseId: idSchema.nullable(),
  recurringPeriod: z.string().nullable(),
  voidedAt: z.string().nullable(),
  voidedByName: z.string().nullable(),
  voidReason: z.string().nullable(),
  createdByName: z.string().nullable().describe('Null for one the worker posted from a recurring expense'),
  locked: z.boolean().describe('Its month is closed: it cannot change, be voided or take receipts until an owner or admin reopens the month'),
  attachments: z.array(expenseAttachmentSchema).describe('Oldest first'),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ExpenseDto = z.infer<typeof expenseSchema>;

export const expenseListSchema = pageSchema(expenseSchema).extend({
  totals: z.object({
    amountMinor: z.number().int().describe('Every matching expense that is not voided, across all pages'),
    count: z.number().int(),
    voidedCount: z.number().int(),
  }),
});
export type ExpenseListDto = z.infer<typeof expenseListSchema>;

export const createRecurringExpenseSchema = z.object({
  categoryId: idSchema,
  locationId: idSchema.nullable().optional(),
  amountMinor: amountSchema,
  supplier: textOrNull(160),
  method: z.enum(EXPENSE_METHODS),
  note: textOrNull(2000),
  frequency: z.enum(RECURRING_EXPENSE_FREQUENCIES),
  dayOfMonth: z.number().int().min(1).max(31).describe('A day past the end of a short month falls on its last day'),
  monthOfYear: z.number().int().min(1).max(12).nullable().optional().describe('Yearly only'),
  startsOn: dateOnlySchema.describe('The first date it may post on; every period from here to today that has not posted yet posts on the next run'),
  endsOn: dateOnlySchema.nullable().optional(),
  active: z.boolean().default(true),
});
export type CreateRecurringExpenseInput = z.infer<typeof createRecurringExpenseSchema>;
export const updateRecurringExpenseSchema = patchOf(createRecurringExpenseSchema);
export type UpdateRecurringExpenseInput = z.infer<typeof updateRecurringExpenseSchema>;

export const recurringExpenseSchema = z.object({
  id: idSchema,
  categoryId: idSchema,
  categoryName: z.string(),
  categoryNameLocalized: z.record(z.string(), z.string()),
  locationId: idSchema.nullable(),
  locationName: z.string().nullable(),
  amountMinor: z.number().int(),
  currency: z.string(),
  supplier: z.string().nullable(),
  method: z.enum(EXPENSE_METHODS),
  note: z.string().nullable(),
  frequency: z.enum(RECURRING_EXPENSE_FREQUENCIES),
  dayOfMonth: z.number().int(),
  monthOfYear: z.number().int().nullable(),
  startsOn: z.string(),
  endsOn: z.string().nullable(),
  active: z.boolean(),
  nextDate: z.string().nullable().describe('The next date it posts on with the values above; null once it has ended or while it is off'),
  lastPostedOn: z.string().nullable(),
  postedCount: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type RecurringExpenseDto = z.infer<typeof recurringExpenseSchema>;

export const PROFIT_MAX_MONTHS = 36;

export const profitQuerySchema = z.object({
  from: monthSchema.optional().describe('First month, YYYY-MM; eleven months before `to` when left out'),
  to: monthSchema.optional().describe('Last month, YYYY-MM; the current month when left out'),
});
export type ProfitQuery = z.infer<typeof profitQuerySchema>;

const profitFiguresShape = {
  membershipsMinor: z.number().int().describe('Net membership revenue: what the finance summary counts, payments less completed refunds'),
  barMinor: z.number().int().describe('Bar sales less bar refunds, balance sales included; 0 while the bar is off'),
  incomeMinor: z.number().int(),
  expensesMinor: z.number().int().describe('Expenses that are not voided'),
  payrollMinor: z.number().int().describe("The month's payroll run: its cost to the gym (gross plus the employer's pension); 0 while payroll is off"),
  profitMinor: z.number().int().describe('Income less expenses less payroll'),
};

export const profitRowSchema = z.object({ month: z.string(), locationId: idSchema.nullable().describe('Null: the whole gym, for online payments, expenses of no branch and payroll'), ...profitFiguresShape });
export type ProfitRowDto = z.infer<typeof profitRowSchema>;

export const profitMonthSchema = z.object({
  month: z.string(),
  closed: z.boolean(),
  payrollStatus: z.enum(PAYROLL_RUN_STATUSES).nullable().describe('The status of the run counted, so a draft can be told apart; null when the month has none'),
  ...profitFiguresShape,
});
export type ProfitMonthDto = z.infer<typeof profitMonthSchema>;

export const profitSchema = z.object({
  currency: z.string(),
  from: z.string(),
  to: z.string(),
  branches: z.array(z.string()).nullable().describe('The branches counted, for someone who works only at some; null for the whole gym'),
  includes: z.object({ bar: z.boolean(), payroll: z.boolean() }),
  locations: z.array(z.object({ id: idSchema.nullable(), name: z.string().nullable() })).describe('Every place the rows name, the whole gym (id null) last'),
  rows: z.array(profitRowSchema).describe('One per month and place that had anything'),
  months: z.array(profitMonthSchema).describe('Every month of the period, oldest first, zeros included'),
  totals: z.object(profitFiguresShape),
});
export type ProfitDto = z.infer<typeof profitSchema>;
